/**
 * Shapes the food models share (05 §5): a lathe, an extruded outline, a ring, a tube and small
 * helpers. Round surfaces carry smooth vertex normals so the toon bands follow the curve; where a
 * profile or outline turns sharply (a rim, a cut edge) the crease stays hard, as it should.
 * Local frame as everywhere: y up, +z toward the camera, base on y = 0.
 */

import { type Color, Matrix4, Vector3 } from 'three';
import type { MeshBuilder } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';

/** Builds one item into `mb` with colours from `p`. */
export type Builder = (mb: MeshBuilder, p: Palette) => void;

/** A face colour: fixed, or chosen by (segment or edge index, side index) for bands and stripes. */
export type Paint = Color | ((seg: number, side: number) => Color);
const paint = (c: Paint, seg: number, side: number): Color => (typeof c === 'function' ? c(seg, side) : c);

/** Neighbouring faces meeting at less than this cosine keep a hard edge (about 45 degrees). */
const CREASE = 0.7;

/** Translate to (x, y, z), then turn by `roll` about z. */
export const rotZ = (x: number, y: number, roll: number, z = 0): Matrix4 =>
  new Matrix4().makeTranslation(x, y, z).multiply(new Matrix4().makeRotationZ(roll));

/** Translate to (x, y, z), then scale non-uniformly. */
export const scaled = (x: number, y: number, z: number, sx: number, sy: number, sz: number): Matrix4 =>
  new Matrix4().makeTranslation(x, y, z).multiply(new Matrix4().makeScale(sx, sy, sz));

/** Transform chain: `chain(a, b, c)` applies c first, then b, then a (a is outermost). */
export const chain = (...ms: Matrix4[]): Matrix4 => ms.reduce((acc, m) => acc.multiply(m), new Matrix4());

/** Turns a model built along +y so it points at the camera (+z). */
export const faceCamera = (): Matrix4 => new Matrix4().makeRotationX(Math.PI / 2);

type Pt = readonly [number, number];

/** Per-vertex normals of a polyline's segments: [start, end] per segment, joined where smooth. */
function segmentNormals(pts: readonly Pt[], closed: boolean): Array<[Pt, Pt]> {
  const n = pts.length;
  const segs = closed ? n : n - 1;
  const own: Pt[] = [];
  for (let i = 0; i < segs; i++) {
    const a = pts[i] ?? [0, 0];
    const b = pts[(i + 1) % n] ?? [0, 0];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    own.push([dy / len, -dx / len]);
  }
  const join = (s: number, t: number): Pt | null => {
    const u = own[s];
    const v = own[t];
    if (!u || !v || u[0] * v[0] + u[1] * v[1] < CREASE) return null;
    const x = u[0] + v[0];
    const y = u[1] + v[1];
    const len = Math.hypot(x, y) || 1;
    return [x / len, y / len];
  };
  return own.map((nrm, s) => {
    const prev = s > 0 ? s - 1 : closed ? segs - 1 : -1;
    const next = s < segs - 1 ? s + 1 : closed ? 0 : -1;
    const start = prev >= 0 ? (join(prev, s) ?? nrm) : nrm;
    const end = next >= 0 ? (join(s, next) ?? nrm) : nrm;
    return [start, end];
  });
}

export interface LatheOpts {
  sides?: number;
  /** Angle of the first side (radians). */
  phase?: number;
  /** Flat facets (cut gems) instead of a smooth surface. */
  flat?: boolean;
  /** Extra turn per profile point (radians), so facets between rings become triangles. */
  twist?: readonly number[];
}

/**
 * A surface of revolution about y: `profile` is (radius, height) from bottom to top. A radius of
 * 0 closes the surface at the axis. `color` may pick a colour per profile segment and side.
 */
export function lathe(mb: MeshBuilder, profile: readonly Pt[], color: Paint, m?: Matrix4, opts: LatheOpts = {}): void {
  const { sides = 20, phase = 0, flat = false, twist } = opts;
  const normals = segmentNormals(profile, false);
  const pos = (i: number, a: number): Vector3 => {
    const [r, y] = profile[i] ?? [0, 0];
    return new Vector3(Math.sin(a) * r, y, Math.cos(a) * r);
  };
  const nrm = (n: Pt, a: number): Vector3 => new Vector3(Math.sin(a) * n[0], n[1], Math.cos(a) * n[0]);
  const build = (): void => {
    for (let s = 0; s + 1 < profile.length; s++) {
      const [n0, n1] = normals[s] ?? [[1, 0], [1, 0]];
      const r0 = profile[s]?.[0] ?? 0;
      const r1 = profile[s + 1]?.[0] ?? 0;
      const t0 = phase + (twist?.[s] ?? 0);
      const t1 = phase + (twist?.[s + 1] ?? 0);
      for (let k = 0; k < sides; k++) {
        const a0 = (k / sides) * Math.PI * 2;
        const a1 = ((k + 1) / sides) * Math.PI * 2;
        const c = paint(color, s, k);
        const A = pos(s, a0 + t0);
        const B = pos(s, a1 + t0);
        const C = pos(s + 1, a1 + t1);
        const D = pos(s + 1, a0 + t1);
        if (flat) {
          if (r0 > 1e-6) mb.tri(A, B, C, c);
          if (r1 > 1e-6) mb.tri(A, C, D, c);
        } else {
          const nA = nrm(n0, a0 + t0);
          const nB = nrm(n0, a1 + t0);
          const nC = nrm(n1, a1 + t1);
          const nD = nrm(n1, a0 + t1);
          if (r0 > 1e-6) mb.triSmooth(A, B, C, nA, nB, nC, c);
          if (r1 > 1e-6) mb.triSmooth(A, C, D, nA, nC, nD, c);
        }
      }
    }
  };
  if (m) mb.with(m, build);
  else build();
}

/**
 * A closed outline in the x-y plane (counter-clockwise, star-shaped about its centroid) extruded
 * from z = -depth/2 to +depth/2: smooth sides, flat front and back. `color` picks per edge.
 */
export function extrude(
  mb: MeshBuilder, outline: readonly Pt[], depth: number, color: Paint, m?: Matrix4, front?: Color, back?: Color,
): void {
  const normals = segmentNormals(outline, true);
  const n = outline.length;
  const zf = depth / 2;
  let cx = 0;
  let cy = 0;
  for (const [x, y] of outline) {
    cx += x / n;
    cy += y / n;
  }
  const build = (): void => {
    for (let i = 0; i < n; i++) {
      const [ax, ay] = outline[i] ?? [0, 0];
      const [bx, by] = outline[(i + 1) % n] ?? [0, 0];
      const [na, nb] = normals[i] ?? [[0, 1], [0, 1]];
      const c = paint(color, i, 0);
      const A = new Vector3(ax, ay, -zf);
      const B = new Vector3(bx, by, -zf);
      const C = new Vector3(bx, by, zf);
      const D = new Vector3(ax, ay, zf);
      const nA = new Vector3(na[0], na[1], 0);
      const nB = new Vector3(nb[0], nb[1], 0);
      mb.triSmooth(A, B, C, nA, nB, nB, c);
      mb.triSmooth(A, C, D, nA, nB, nA, c);
      mb.tri(new Vector3(cx, cy, zf), D, C, front ?? c);
      mb.tri(new Vector3(cx, cy, -zf), B, A, back ?? front ?? c);
    }
  };
  if (m) mb.with(m, build);
  else build();
}

/**
 * A ring about the z axis (a donut facing the camera): radius `R` to the tube's centre, tube
 * radius `r`. `color` may pick by the surface normal (x, y, z), e.g. icing on the front half.
 */
export function torus(
  mb: MeshBuilder, R: number, r: number, color: Color | ((n: Vector3) => Color), m?: Matrix4, segs = 24, tube = 12,
  arc = Math.PI * 2, arcStart = 0,
): void {
  const point = (u: number, v: number): { p: Vector3; n: Vector3 } => {
    const n = new Vector3(Math.cos(u) * Math.cos(v), Math.sin(u) * Math.cos(v), Math.sin(v));
    const p = new Vector3(Math.cos(u) * R, Math.sin(u) * R, 0).addScaledVector(n, r);
    return { p, n };
  };
  const build = (): void => {
    for (let i = 0; i < segs; i++) {
      const u0 = arcStart + (i / segs) * arc;
      const u1 = arcStart + ((i + 1) / segs) * arc;
      for (let j = 0; j < tube; j++) {
        const v0 = (j / tube) * Math.PI * 2;
        const v1 = ((j + 1) / tube) * Math.PI * 2;
        const a = point(u0, v0);
        const b = point(u1, v0);
        const c = point(u1, v1);
        const d = point(u0, v1);
        const mid = a.n.clone().add(c.n).normalize();
        const col = typeof color === 'function' ? color(mid) : color;
        mb.triSmooth(a.p, b.p, c.p, a.n, b.n, c.n, col);
        mb.triSmooth(a.p, c.p, d.p, a.n, c.n, d.n, col);
      }
    }
  };
  if (m) mb.with(m, build);
  else build();
}

/**
 * A smooth tube through `points` with a radius per point (stems, a banana, a drumstick). Ends
 * with a radius above zero get a flat cap.
 */
export function tube(
  mb: MeshBuilder, points: readonly Vector3[], radii: readonly number[], color: Paint, sides = 12, m?: Matrix4,
): void {
  const rings: Array<Array<{ p: Vector3; n: Vector3 }>> = [];
  let side = new Vector3();
  points.forEach((pt, i) => {
    const next = points[Math.min(points.length - 1, i + 1)] ?? pt;
    const prev = points[Math.max(0, i - 1)] ?? pt;
    const t = next.clone().sub(prev).normalize();
    if (i === 0) {
      const ref = Math.abs(t.z) < 0.9 ? new Vector3(0, 0, 1) : new Vector3(1, 0, 0);
      side = ref.clone().cross(t).normalize();
    } else {
      side = side.clone().addScaledVector(t, -side.dot(t)).normalize();
    }
    const up = t.clone().cross(side).normalize();
    const r = radii[i] ?? 0;
    const ring: Array<{ p: Vector3; n: Vector3 }> = [];
    for (let k = 0; k < sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      const n = side.clone().multiplyScalar(Math.cos(a)).addScaledVector(up, Math.sin(a));
      ring.push({ p: pt.clone().addScaledVector(n, r), n });
    }
    rings.push(ring);
  });
  const build = (): void => {
    for (let i = 0; i + 1 < rings.length; i++) {
      const a = rings[i] ?? [];
      const b = rings[i + 1] ?? [];
      for (let k = 0; k < sides; k++) {
        const a0 = a[k];
        const a1 = a[(k + 1) % sides];
        const b0 = b[k];
        const b1 = b[(k + 1) % sides];
        if (!a0 || !a1 || !b0 || !b1) continue;
        const c = paint(color, i, k);
        mb.triSmooth(a0.p, a1.p, b1.p, a0.n, a1.n, b1.n, c);
        mb.triSmooth(a0.p, b1.p, b0.p, a0.n, b1.n, b0.n, c);
      }
    }
    type Ring = Array<{ p: Vector3 }> | undefined;
    const cap = (ring: Ring, centre: Vector3 | undefined, flip: boolean, c: Color): void => {
      if (!ring || !centre) return;
      for (let k = 0; k < sides; k++) {
        const p0 = ring[k]?.p;
        const p1 = ring[(k + 1) % sides]?.p;
        if (!p0 || !p1) continue;
        if (flip) mb.tri(centre, p1, p0, c);
        else mb.tri(centre, p0, p1, c);
      }
    };
    const last = points.length - 1;
    if ((radii[0] ?? 0) > 1e-3) cap(rings[0], points[0], true, paint(color, 0, 0));
    if ((radii[last] ?? 0) > 1e-3) cap(rings[last], points[last], false, paint(color, last - 1, 0));
  };
  if (m) mb.with(m, build);
  else build();
}

/**
 * A leaf: a flattened, pointed blade growing from (x, y, z) along its own +y, turned by `roll`
 * about z and `yaw` about y, `len` long. Flat in its local z, so it faces the camera at yaw 0.
 */
export function leaf(
  mb: MeshBuilder, color: Color, x: number, y: number, z: number, len: number, width: number, roll: number, yaw = 0,
  thick = 0.3,
): void {
  const m = new Matrix4().makeTranslation(x, y, z)
    .multiply(new Matrix4().makeRotationY(yaw))
    .multiply(new Matrix4().makeRotationZ(roll));
  const w = width / 2;
  // A lens-shaped blade: widest a third of the way up, pointed at the tip.
  const outline: Pt[] = [
    [0, 0], [w * 0.7, len * 0.15], [w, len * 0.38], [w * 0.8, len * 0.62], [w * 0.4, len * 0.85], [0, len],
    [-w * 0.4, len * 0.85], [-w * 0.8, len * 0.62], [-w, len * 0.38], [-w * 0.7, len * 0.15],
  ];
  extrude(mb, outline, width * thick, color, m);
}

/** Points on a circle-ish profile: `n` steps of a quarter/half ellipse from angle a0 to a1 (radians). */
export function arcPts(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]);
  }
  return out;
}
